// C9 — the page an email confirmation link lands on.
//
// Deliberately dumb: it reads the token from the URL, calls the API once, and
// reports the outcome. All the judgement (expired vs used vs already-confirmed,
// and whether a parked subscription gets applied) lives on the server, so this
// cannot disagree with it.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const [tier, setTier] = useState(null);

  // React 18 StrictMode double-invokes effects in development, and this one
  // spends a single-use token. Without the guard the second call reports "this
  // link has already been used" for a link that just worked.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (!token) {
      setState('error');
      setMessage('This link is missing its confirmation code. Try the link in your email again.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();

        if (res.ok) {
          setState('success');
          setMessage(data.message || 'Email address confirmed.');
          setTier(data.subscription_tier || null);
        } else {
          setState('error');
          setMessage(data.error || 'We could not confirm this email address.');
        }
      } catch {
        // A network failure is not a bad token — say so, or the user will
        // assume the link is spent and stop trying it.
        setState('error');
        setMessage('We could not reach the server. Check your connection and try the link again.');
      }
    })();
  }, [token]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Email confirmation</h2>

        {state === 'verifying' && <p>Confirming your email address…</p>}

        {state === 'success' && (
          <>
            <div className="success">{message}</div>
            {tier && (
              <p>
                Your <strong>{tier}</strong> subscription has been applied to this account.
              </p>
            )}
            <Link to="/login" className="auth-link">Continue to sign in</Link>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="error">{message}</div>
            <p>
              Need a new link? Sign in and use <strong>Resend confirmation email</strong>,
              or request one from the sign-in page.
            </p>
            <Link to="/login" className="auth-link">Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
